import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../utils';
import formatNumber, {
  defaultFilterDates,
  formatDate,
  DUO_PERIOD_OPTIONS,
  getPeriodDateRange,
} from '../../utils/functions';
import DuoModal from '../../components/ui/duo-modal';
import DuoPeriodPicker from '../../components/ui/duo-period-picker';

const PER_PAGE = 10;
const today = new Date().toISOString().split('T')[0];

const PERIOD_ALL = { label: 'Tout', value: '' };
const RAPPORT_EXTRA_PERIODS = [
  { label: 'Cette semaine', value: 'this_week' },
  { label: 'Ce trimestre', value: 'this_quarter' },
  { label: 'Cette année', value: 'this_year' },
];
const periodOptionsWithAll = [PERIOD_ALL, ...DUO_PERIOD_OPTIONS, ...RAPPORT_EXTRA_PERIODS];

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const localYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const getStartOfQuarter = (date) => {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1);
};

const buildReportApiParams = (filters) => {
  const params = {};
  if (filters.period) {
    const range = getPeriodDateRange(filters.period);
    if (range) {
      if (range.from) params.start_date = localYmd(range.from);
      if (range.to) params.end_date = localYmd(range.to);
      return params;
    }
    const now = new Date();
    const todayStr = localYmd(now);
    switch (filters.period) {
      case 'this_week':
        params.start_date = localYmd(getStartOfWeek(new Date(now)));
        params.end_date = todayStr;
        break;
      case 'this_quarter':
        params.start_date = localYmd(getStartOfQuarter(new Date(now)));
        params.end_date = todayStr;
        break;
      case 'this_year':
        params.start_date = localYmd(new Date(now.getFullYear(), 0, 1));
        params.end_date = todayStr;
        break;
      default:
        break;
    }
    return params;
  }
  if (filters.specificDate) {
    params.start_date = filters.specificDate;
    params.end_date = filters.specificDate;
    return params;
  }
  if (filters.dateFrom) params.start_date = filters.dateFrom;
  if (filters.dateTo) params.end_date = filters.dateTo;
  return params;
};

const calculateTotalSommaire = (report) =>
  report.sommaires?.reduce((sum, item) => sum + parseFloat(item.montant || 0), 0) ?? 0;

const calculateTotalDepenses = (report) =>
  report.depenses?.reduce((sum, item) => sum + parseFloat(item.montant || 0), 0) ?? 0;

const calculateTotalVersements = (report) =>
  report.versements?.reduce((sum, item) => sum + parseFloat(item.montant || 0), 0) ?? 0;

const calculateTotalReste = (report) =>
  calculateTotalDepenses(report) + calculateTotalVersements(report) - calculateTotalSommaire(report);

const emptyForm = () => ({
  date: today,
  sommaires: [{ designation: '', montant: 0 }],
  depenses: [{ designation: '', montant: 0 }],
  versements: [{ designation: '', montant: 0, file: null, existingFilePath: null }],
});

const reportToForm = (report) => ({
  date: (report.date || today).toString().split('T')[0],
  sommaires:
    report.sommaires?.length > 0
      ? report.sommaires.map((s) => ({
          designation: s.designation || '',
          montant: parseFloat(s.montant) || 0,
        }))
      : [{ designation: '', montant: 0 }],
  depenses:
    report.depenses?.length > 0
      ? report.depenses.map((d) => ({
          designation: d.designation || '',
          montant: parseFloat(d.montant) || 0,
        }))
      : [{ designation: '', montant: 0 }],
  versements:
    report.versements?.length > 0
      ? report.versements.map((v) => ({
          designation: v.designation || '',
          montant: parseFloat(v.montant) || 0,
          file: null,
          existingFilePath: v.file_path || null,
        }))
      : [{ designation: '', montant: 0, file: null, existingFilePath: null }],
});

const receiptUrl = (path) => (path ? `${APP_API_URL.replace('/api', '/')}${path}` : null);

const getSortValue = (item, key) => {
  if (key === 'user') return item.user?.name ?? '';
  if (key === 'total_sommaire') return calculateTotalSommaire(item);
  if (key === 'total_depenses') return calculateTotalDepenses(item);
  if (key === 'total_versements') return calculateTotalVersements(item);
  if (key === 'total_reste') return calculateTotalReste(item);
  if (key === 'date') return item.date ?? '';
  return item[key] ?? '';
};

const canManageBusinessReports = (user) =>
  user?.role === 'Administrateur' || user?.role === 'superAdmin';

export default function ReportsAdmin() {
  const user = useSelector((state) => state.auth.user);
  const canManage = canManageBusinessReports(user);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_ALL);
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    search: '',
    period: '',
    specificDate: '',
    ...defaultFilterDates(),
  }));
  const [draftFilters, setDraftFilters] = useState(() => ({
    specificDate: '',
    ...defaultFilterDates(),
  }));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedReport, setSelectedReport] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dateNotice, setDateNotice] = useState('');

  const fetchData = useCallback(
    async (filterOverride) => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = buildReportApiParams(filterOverride ?? appliedFilters);
        const response = await axios.get(`${APP_API_URL}/reports-admin`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        setData(response.data || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des rapports :', error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters]
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!formModalOpen || editingReport) {
      return undefined;
    }

    const loadDefaultSommaires = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${APP_API_URL}/default-sommaires`, {
          params: { date: formData.date },
          headers: { Authorization: `Bearer ${token}` },
        });
        setDateNotice(response.data.date_adjusted_message || '');
        const nextDate = response.data.suggested_date || formData.date;
        setFormData((prev) => ({
          ...prev,
          date: nextDate,
          sommaires: response.data.sommaires?.length
            ? response.data.sommaires
            : [{ designation: '', montant: 0 }],
          depenses: response.data.depenses?.length ? response.data.depenses : prev.depenses,
        }));
      } catch (error) {
        console.error('Erreur chargement sommaires par défaut :', error);
        setDateNotice('');
      }
    };

    loadDefaultSommaires();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formModalOpen, editingReport, formData.date]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredItems = useMemo(() => {
    const search = appliedFilters.search.trim().toLowerCase();
    if (!search) return data;
    return data.filter((item) => {
      const haystack = [(item.user?.name || ''), item.date || ''].join(' ').toLowerCase();
      return haystack.includes(search);
    });
  }, [data, appliedFilters.search]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (
        key === 'total_sommaire' ||
        key === 'total_depenses' ||
        key === 'total_versements' ||
        key === 'total_reste'
      ) {
        return (aValue - bValue) * dir;
      }
      if (key === 'date') return (new Date(aValue) - new Date(bValue)) * dir;
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return -1 * dir;
      if (aStr > bStr) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      count: filteredItems.length,
      totalSommaire: filteredItems.reduce((acc, item) => acc + calculateTotalSommaire(item), 0),
      totalReste: filteredItems.reduce((acc, item) => acc + calculateTotalReste(item), 0),
    }),
    [filteredItems]
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PER_PAGE));
  const indexOfLast = currentPage * PER_PAGE;
  const indexOfFirst = indexOfLast - PER_PAGE;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handlePeriodSelect = (option) => {
    setSelectedPeriod(option);
    setDraftFilters({ specificDate: '', dateFrom: '', dateTo: '' });
    const next = {
      search: appliedFilters.search,
      period: option.value,
      specificDate: '',
      dateFrom: '',
      dateTo: '',
    };
    setAppliedFilters(next);
    setCurrentPage(1);
    fetchData(next);
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setSelectedPeriod(PERIOD_ALL);
    const next = {
      search: appliedFilters.search,
      period: '',
      specificDate: draftFilters.specificDate,
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
    };
    setAppliedFilters(next);
    setCurrentPage(1);
    fetchData(next);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSelectedPeriod(PERIOD_ALL);
    setDraftFilters({ specificDate: '', ...defaultFilterDates() });
    const next = { search: '', period: '', specificDate: '', ...defaultFilterDates() };
    setAppliedFilters(next);
    setCurrentPage(1);
    setSortConfig({ key: 'date', direction: 'desc' });
    fetchData(next);
  };

  const openCreateModal = () => {
    setEditingReport(null);
    setFormData(emptyForm());
    setFormErrors({});
    setDateNotice('');
    setFormModalOpen(true);
  };

  const openEditModal = (report) => {
    setEditingReport(report);
    setFormData(reportToForm(report));
    setFormErrors({});
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingReport(null);
    setFormData(emptyForm());
    setFormErrors({});
    setDateNotice('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateLine = (section, index, e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => {
      const lines = [...prev[section]];
      if (name === 'file') {
        lines[index] = { ...lines[index], file: files[0] || null };
      } else {
        lines[index] = { ...lines[index], [name]: name === 'montant' ? value : value };
      }
      return { ...prev, [section]: lines };
    });
  };

  const addLine = (section) => {
    const template =
      section === 'versements'
        ? { designation: '', montant: 0, file: null, existingFilePath: null }
        : { designation: '', montant: 0 };
    setFormData((prev) => ({ ...prev, [section]: [...prev[section], template] }));
  };

  const removeLine = (section, index) => {
    setFormData((prev) => {
      const lines = [...prev[section]];
      if (lines.length <= 1) return prev;
      lines.splice(index, 1);
      return { ...prev, [section]: lines };
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.date) errors.date = 'Date requise';
    if (formData.sommaires.length < 1) errors.sommaires = 'Au moins un sommaire requis';
    formData.sommaires.forEach((item, index) => {
      if (!item.designation) errors[`sommaire_designation_${index}`] = 'Désignation requise';
      if (parseFloat(item.montant) < 0) errors[`sommaire_montant_${index}`] = 'Montant invalide';
    });
    if (formData.depenses.length < 1) errors.depenses = 'Au moins une dépense requise';
    formData.depenses.forEach((item, index) => {
      if (!item.designation) errors[`depense_designation_${index}`] = 'Désignation requise';
      if (parseFloat(item.montant) < 0) errors[`depense_montant_${index}`] = 'Montant invalide';
    });
    if (formData.versements.length < 1) errors.versements = 'Au moins un versement requis';
    formData.versements.forEach((item, index) => {
      if (!item.designation) errors[`versement_designation_${index}`] = 'Désignation requise';
      if (parseFloat(item.montant) < 0) errors[`versement_montant_${index}`] = 'Montant invalide';
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const appendFormData = () => {
    const fd = new FormData();
    fd.append('date', formData.date);
    fd.append('sommaires', JSON.stringify(formData.sommaires));
    fd.append('depenses', JSON.stringify(formData.depenses));
    formData.versements.forEach((versement, index) => {
      fd.append(`versements[${index}][designation]`, versement.designation);
      fd.append(`versements[${index}][montant]`, versement.montant);
      if (versement.file) {
        fd.append(`versements[${index}][file]`, versement.file);
      } else if (versement.existingFilePath) {
        fd.append(`versements[${index}][existing_file_path]`, versement.existingFilePath);
      }
    });
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = appendFormData();
      if (editingReport?.id) {
        await axios.put(`${APP_API_URL}/reports-admin/${editingReport.id}`, formDataToSend, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        Swal.fire({ icon: 'success', title: 'Rapport mis à jour', timer: 1500, showConfirmButton: false });
      } else {
        await axios.post(`${APP_API_URL}/reports-admin`, formDataToSend, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        Swal.fire({ icon: 'success', title: 'Rapport créé', timer: 1500, showConfirmButton: false });
      }
      closeFormModal();
      fetchData();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.error || error.response?.data?.message || 'Enregistrement impossible.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (report) => {
    const result = await Swal.fire({
      title: 'Supprimer ce rapport ?',
      text: `Rapport du ${formatDate(report.date)} — ${report.user?.name || ''}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff4b4b',
      cancelButtonText: 'Annuler',
      confirmButtonText: 'Supprimer',
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${APP_API_URL}/reports-admin/${report.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (selectedReport?.id === report.id) setSelectedReport(null);
      Swal.fire({ icon: 'success', title: 'Rapport supprimé', timer: 1500, showConfirmButton: false });
      fetchData();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.error || 'Suppression impossible.',
      });
    }
  };

  const exportRows = (items) =>
    items.map((item) => ({
      Date: formatDate(item.date),
      Personnel: item.user?.name ?? '—',
      Sommaire: `${formatNumber(calculateTotalSommaire(item))} FCFA`,
      Dépenses: `${formatNumber(calculateTotalDepenses(item))} FCFA`,
      Versements: `${formatNumber(calculateTotalVersements(item))} FCFA`,
      Reste: `${formatNumber(calculateTotalReste(item))} FCFA`,
    }));

  const exportToPDF = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text('Rapports activité', 14, 16);
    doc.autoTable({
      head: [['Date', 'Personnel', 'Sommaire', 'Dépenses', 'Versements', 'Reste']],
      body: rows.map((r) => [r.Date, r.Personnel, r.Sommaire, r.Dépenses, r.Versements, r.Reste]),
      startY: 24,
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 8 },
    });
    doc.save(`Rapports activité ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rapports');
    XLSX.writeFile(wb, `Rapports activité ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = rows
      .map(
        (r) =>
          `<tr><td>${r.Date}</td><td>${r.Personnel}</td><td>${r.Sommaire}</td><td>${r.Dépenses}</td><td>${r.Versements}</td><td>${r.Reste}</td></tr>`
      )
      .join('');
    printWindow.document.write(`
      <html><head><title>Rapports</title>
      <style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px;font-size:12px}</style>
      </head><body><h1>Rapports activité</h1><table>
      <thead><tr><th>Date</th><th>Personnel</th><th>Sommaire</th><th>Dépenses</th><th>Versements</th><th>Reste</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const activePeriodLabel = appliedFilters.period
    ? periodOptionsWithAll.find((p) => p.value === appliedFilters.period)?.label
    : null;

  const formTotalSommaire = formData.sommaires.reduce((s, i) => s + parseFloat(i.montant || 0), 0);
  const formTotalDepenses = formData.depenses.reduce((s, i) => s + parseFloat(i.montant || 0), 0);
  const formTotalVersements = formData.versements.reduce((s, i) => s + parseFloat(i.montant || 0), 0);
  const formTotalReste = formTotalDepenses + formTotalVersements - formTotalSommaire;

  const lineErrorPrefix = (section) => {
    if (section === 'sommaires') return 'sommaire';
    if (section === 'depenses') return 'depense';
    return 'versement';
  };

  const renderLineSection = (section, label, withFile = false) => (
    <div className="mb-3">
      <label className="duo-form-label">{label}</label>
      {formData[section].map((item, index) => {
        const prefix = lineErrorPrefix(section);
        return (
        <div className="row g-2 mb-2 align-items-end" key={`${section}-${index}`}>
          <div className={withFile ? 'col-md-4' : 'col-md-5'}>
            <input
              type="text"
              name="designation"
              placeholder="Désignation"
              className="duo-input"
              value={item.designation}
              onChange={(e) => updateLine(section, index, e)}
            />
            {formErrors[`${prefix}_designation_${index}`] && (
              <small className="text-danger">{formErrors[`${prefix}_designation_${index}`]}</small>
            )}
          </div>
          <div className={withFile ? 'col-md-3' : 'col-md-5'}>
            <input
              type="number"
              name="montant"
              min="0"
              placeholder="Montant"
              className="duo-input"
              value={item.montant}
              onChange={(e) => updateLine(section, index, e)}
            />
          </div>
          {withFile && (
            <div className="col-md-3">
              <input type="file" name="file" className="duo-input" onChange={(e) => updateLine(section, index, e)} />
              {item.existingFilePath && !item.file && (
                <a href={receiptUrl(item.existingFilePath)} target="_blank" rel="noreferrer" className="small">
                  Reçu actuel
                </a>
              )}
            </div>
          )}
          <div className="col-md-2">
            <button
              type="button"
              className="duo-btn duo-btn--ghost duo-btn--sm w-100"
              style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }}
              onClick={() => removeLine(section, index)}
              disabled={formData[section].length === 1}
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        </div>
        );
      })}
      <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => addLine(section)}>
        <i className="bi bi-plus-circle" /> Ajouter
      </button>
      {formErrors[section] && <small className="text-danger d-block">{formErrors[section]}</small>}
    </div>
  );

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Gestion</p>
          <h1 className="duo-hero-title">Rapports d&apos;activité</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Sommaires, dépenses et versements bancaires par jour.
            {canManage && ' Vous pouvez modifier ou supprimer les rapports.'}
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Rapports (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.count}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total sommaire XOF</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(stats.totalSommaire)}
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total reste XOF</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(stats.totalReste)}
            </p>
          </div>
        </div>

        <div className="mb-3">
          <DuoPeriodPicker
            options={periodOptionsWithAll}
            selectedOption={selectedPeriod}
            onSelect={handlePeriodSelect}
          />
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des rapports
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={() => fetchData()} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {canManage && (
                <button type="button" className="duo-btn duo-btn--primary" onClick={openCreateModal}>
                  <i className="bi bi-plus-circle" /> Ajouter
                </button>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher par date ou personnel..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <div>
                    <label className="duo-form-label">Date précise</label>
                    <input
                      type="date"
                      className="duo-input"
                      disabled={!!appliedFilters.period}
                      value={draftFilters.specificDate}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          specificDate: e.target.value,
                          dateFrom: '',
                          dateTo: '',
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de début</label>
                    <input
                      type="date"
                      className="duo-input"
                      disabled={!!appliedFilters.period || !!draftFilters.specificDate}
                      value={draftFilters.dateFrom}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value, specificDate: '' }))
                      }
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de fin</label>
                    <input
                      type="date"
                      className="duo-input"
                      disabled={!!appliedFilters.period || !!draftFilters.specificDate}
                      value={draftFilters.dateTo}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value, specificDate: '' }))
                      }
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer
                  </button>
                </div>
              </form>
            </div>
          )}

          {(activePeriodLabel || appliedFilters.specificDate || appliedFilters.dateFrom || appliedFilters.dateTo) && (
            <div className="px-3 py-2 border-bottom small text-muted" style={{ borderColor: '#f0f0f0' }}>
              {activePeriodLabel && (
                <span>
                  Période : <strong>{activePeriodLabel}</strong>
                </span>
              )}
              {appliedFilters.specificDate && (
                <span>
                  {' '}
                  · Date : <strong>{appliedFilters.specificDate}</strong>
                </span>
              )}
              {(appliedFilters.dateFrom || appliedFilters.dateTo) && !appliedFilters.specificDate && (
                <span>
                  {' '}
                  · Plage : {appliedFilters.dateFrom || '…'} → {appliedFilters.dateTo || '…'}
                </span>
              )}
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('date')}>
                    Date <SortIcon active={isSortActive('date')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('user')}>
                    Personnel <SortIcon active={isSortActive('user')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_sommaire')}>
                    Sommaire <SortIcon active={isSortActive('total_sommaire')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_depenses')}>
                    Dépenses <SortIcon active={isSortActive('total_depenses')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_versements')}>
                    Versements <SortIcon active={isSortActive('total_versements')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_reste')}>
                    Reste <SortIcon active={isSortActive('total_reste')} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id ?? `${item.date}-${item.user?.id}`}>
                      <td className="text-nowrap fw-bold">{formatDate(item.date)}</td>
                      <td>{item.user?.name ?? '—'}</td>
                      <td>{formatNumber(calculateTotalSommaire(item))} XOF</td>
                      <td>{formatNumber(calculateTotalDepenses(item))} XOF</td>
                      <td>{formatNumber(calculateTotalVersements(item))} XOF</td>
                      <td>{formatNumber(calculateTotalReste(item))} XOF</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="duo-btn duo-btn--ghost duo-btn--sm"
                            onClick={() => setSelectedReport(item)}
                          >
                            Détails
                          </button>
                          {canManage && (
                            <>
                              <button
                                type="button"
                                className="duo-btn duo-btn--ghost duo-btn--sm"
                                onClick={() => openEditModal(item)}
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                className="duo-btn duo-btn--ghost duo-btn--sm"
                                style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }}
                                onClick={() => handleDelete(item)}
                              >
                                Supprimer
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="duo-empty-row">
                      Aucun rapport correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      {selectedReport && (
        <DuoModal
          size="lg"
          title={`Rapport — ${formatDate(selectedReport.date)}`}
          onClose={() => setSelectedReport(null)}
          footer={
            <div className="d-flex gap-2 flex-wrap justify-content-end w-100">
              {canManage && (
                <>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    onClick={() => {
                      openEditModal(selectedReport);
                      setSelectedReport(null);
                    }}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }}
                    onClick={() => {
                      handleDelete(selectedReport);
                      setSelectedReport(null);
                    }}
                  >
                    Supprimer
                  </button>
                </>
              )}
              <button type="button" className="duo-btn duo-btn--ghost" onClick={() => setSelectedReport(null)}>
                Fermer
              </button>
            </div>
          }
        >
          <p className="mb-3">
            <span className="text-muted">Personnel :</span>{' '}
            <strong>{selectedReport.user?.name ?? '—'}</strong>
          </p>

          <h6 className="fw-bold">Sommaires</h6>
          <div className="duo-table-wrap mb-3">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Désignation</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {selectedReport.sommaires?.length > 0 ? (
                  selectedReport.sommaires.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.designation}</td>
                      <td>{formatNumber(row.montant)} XOF</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="duo-empty-row">
                      Aucun sommaire
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h6 className="fw-bold">Dépenses</h6>
          <div className="duo-table-wrap mb-3">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Désignation</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {selectedReport.depenses?.length > 0 ? (
                  selectedReport.depenses.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.designation}</td>
                      <td>{formatNumber(row.montant)} XOF</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="duo-empty-row">
                      Aucune dépense
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h6 className="fw-bold">Versements</h6>
          <div className="duo-table-wrap mb-3">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Désignation</th>
                  <th>Montant</th>
                  <th>Reçu</th>
                </tr>
              </thead>
              <tbody>
                {selectedReport.versements?.length > 0 ? (
                  selectedReport.versements.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.designation}</td>
                      <td>{formatNumber(row.montant)} XOF</td>
                      <td>
                        {row.file_path ? (
                          <a href={receiptUrl(row.file_path)} target="_blank" rel="noreferrer">
                            Voir le reçu
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="duo-empty-row">
                      Aucun versement
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="text-end small">
            <p className="mb-1">
              Sommaire : <strong>{formatNumber(calculateTotalSommaire(selectedReport))} XOF</strong>
            </p>
            <p className="mb-1">
              Dépenses : <strong>{formatNumber(calculateTotalDepenses(selectedReport))} XOF</strong>
            </p>
            <p className="mb-1">
              Versements : <strong>{formatNumber(calculateTotalVersements(selectedReport))} XOF</strong>
            </p>
            <p className="mb-0 fw-bold" style={{ fontSize: '1.05rem', color: '#006633' }}>
              Reste : {formatNumber(calculateTotalReste(selectedReport))} XOF
            </p>
          </div>
        </DuoModal>
      )}

      {formModalOpen && (
        <DuoModal
          size="lg"
          title={editingReport ? 'Modifier le rapport' : 'Nouveau rapport'}
          onClose={closeFormModal}
          footer={
            <div className="d-flex gap-2 justify-content-end w-100">
              <button type="button" className="duo-btn duo-btn--ghost" onClick={closeFormModal} disabled={submitting}>
                Annuler
              </button>
              <button type="submit" form="business-report-form" className="duo-btn duo-btn--primary" disabled={submitting}>
                {submitting ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          }
        >
          <form id="business-report-form" onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="duo-form-label">Date</label>
              <input
                type="date"
                name="date"
                className="duo-input"
                value={formData.date}
                onChange={handleInputChange}
              />
              {formErrors.date && <small className="text-danger">{formErrors.date}</small>}
              {dateNotice ? <p className="small text-warning mb-0 mt-1">{dateNotice}</p> : null}
            </div>

            {renderLineSection('sommaires', 'Sommaires')}
            {renderLineSection('depenses', 'Dépenses')}
            {renderLineSection('versements', 'Dépôt à la banque', true)}

            <p className="mb-0 fw-bold" style={{ color: '#006633' }}>
              Total reste : {formatNumber(formTotalReste)} FCFA
            </p>
          </form>
        </DuoModal>
      )}
    </MainLayout>
  );
}
